<template>
    <v-card class="day-collection-widget">
        <nav class="tabs">
            <a class="tab" :class="{'active': currentTab === index}" v-for="(tab, index) in tabs" :key="index"
               @click="changeTab(index)">
                {{ tab }}
            </a>
        </nav>
        <nav class="mobile-arrows">
            <h2 v-html="tabs[currentTab]"/>
            <div class="buttons">
                <v-btn :disabled="loading || currentTab === 0" class="mr-1 navi-btn" elevation="0" x-small
                       @click="changeTab(currentTab - 1)"
                >
                    <v-icon x-small>mdi-chevron-left</v-icon>
                    <span>{{ tabs[currentTab - 1] }}</span>
                </v-btn>
                <v-btn :disabled="loading || currentTab === 6" class="mr-1 navi-btn" elevation="0" x-small
                       @click="changeTab(currentTab + 1)"
                >
                    <span>{{ tabs[currentTab + 1] }}</span>
                    <v-icon x-small>mdi-chevron-right</v-icon>
                </v-btn>
            </div>
        </nav>
        <section aria-label="page-content" class="animated-element" :class="{ 'is-visible': !loading }">
            <template v-if="loading && weekList === ''">
                <v-skeleton-loader
                    type="text"
                    v-for="n in 8"
                    :key="n"
                    style="height: 2.5rem"
                />
            </template>
            <div class="items">
                <div class="itemRow" v-for="(item) in weekList?.edges?.[currentTab].node?.items" :key="item.element.id">
                    <component
                        :data="item.element"
                        :item="item"
                        :is="components[item.element.__typename]"
                    />
                </div>
                <div class="itemRow" v-for="(item) in weekList?.edges?.[currentTab].node?.infoItems" :key="item.element.id">
                    <component
                        :data="item.element"
                        :is="components[item.element.__typename]"
                        :stepsMade="currentTab"
                    />
                </div>
            </div>
        </section>
    </v-card>
</template>

<script setup>
import { onMounted, ref } from 'vue'

import dayCollectionWeekQuery from '@/graphql/querie/dayCollectionWeek.graphql'
import object_dayCollectionCourse from '@/components/fileComponents/templates/dayCollection/object_dayCollectionCourse'
import object_dayCollectionTrack from '@/components/fileComponents/templates/dayCollection/object_dayCollectionTrack'
import object_dayCollectionLocation from '@/components/fileComponents/templates/dayCollection/object_dayCollectionLocation'
import object_dayCollectionInfo from '@/components/fileComponents/templates/dayCollection/object_dayCollectionInfo'
import object_dayCollectionWeather from '@/components/fileComponents/templates/dayCollection/object_dayCollectionWeather'
import object_dayCollectionPosition from '@/components/fileComponents/templates/dayCollection/object_dayCollectionPosition'
import object_dayCollectionMap from '@/components/fileComponents/templates/dayCollection/object_dayCollectionMap'
import { apiClient } from '@/plugins/apollo'

const apiEndpoint = process.env.VUE_APP_API_ENDPOINT

const components = {
    object_dayCollectionCourse,
    object_dayCollectionTrack,
    object_dayCollectionLocation,
    object_dayCollectionInfo,
    object_dayCollectionWeather,
    object_dayCollectionPosition,
    object_dayCollectionMap
}

const loading = ref(false)
const week = ref('')
const weekList = ref('')
const currentTab = ref(0)
const tabs = ref([])

function changeTab(index) {
    if (index === currentTab.value) return
    loading.value = !loading.value
    setTimeout(() => {
        currentTab.value = index
        loading.value = !loading.value
    }, 500)
}

function getWeekDatesInGermanFormat() {
    const dates = []
    const today = new Date()

    for (let i = 0; i < 7; i++) {
        const date = new Date(today)
        date.setDate(today.getDate() + i)
        const formattedDate = `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`
        dates.push(`${formattedDate}`)
    }
    tabs.value = dates
}

function sendWeekRequest() {
    apiClient
        .query({
            query: dayCollectionWeekQuery,
            variables: {
                week: week.value,
            },
            fetchPolicy: 'no-cache',
        })
        .then((response) => {
            weekList.value = response.data.getDayCollectionListing
            loading.value = false
        })
}

function getSevenDays() {
    let daysArray = []
    let today = new Date()

    for (let i = 0; i < 7; i++) {
        let currentDate = new Date()
        currentDate.setDate(today.getDate() + i)
        let formattedDate = `/${apiEndpoint}/Platzinformationen/Days/${currentDate.getFullYear()}/${(currentDate.getMonth() + 1).toString().padStart(2, '0')}/${currentDate.getDate().toString().padStart(2, '0')}`

        daysArray.push(formattedDate)
    }

    week.value = daysArray.join(',')
}

onMounted(() => {
    getSevenDays();
    getWeekDatesInGermanFormat()
    loading.value = true
    sendWeekRequest()
})
</script>

<style lang="scss">
.day-collection-widget {
    padding: 1.5rem;

    .v-dialog__content .v-toolbar__content {
        justify-content: space-between;
    }

    nav.tabs {
        display: flex;
        margin-bottom: 1.5rem;
        border-bottom: 1px solid #dee2e6;
        flex-wrap: wrap;
        padding-left: 0;
        list-style: none;
        @media only screen and (max-width: 48em) {
            display: none;
        }

        a.tab {
            font-size: 1rem;
            margin-bottom: -1px;
            background: 0;
            border: 1px solid transparent;
            border-top-left-radius: .25rem;
            border-top-right-radius: .25rem;
            display: block;
            padding: .5rem 1rem;
            text-decoration: none;
            -webkit-transition: color .15s ease-in-out, background-color .15s ease-in-out, border-color .15s ease-in-out;
            -o-transition: color .15s ease-in-out, background-color .15s ease-in-out, border-color .15s ease-in-out;
            transition: color .15s ease-in-out, background-color .15s ease-in-out, border-color .15s ease-in-out;
            color: #747474;
            cursor: pointer;

            &.active {
                color: #495057;
                background-color: #fff;
                border-color: #dee2e6 #dee2e6 #fff;
            }
        }
    }

    nav.mobile-arrows {
        display: none;
        text-align: center;
        margin-bottom: 1.5rem;
        @media only screen and (max-width: 48em) {
            display: block;
        }

        h2 {
            font-size: 1rem;
            margin-bottom: 0.5rem;
        }
    }

    section[aria-label='page-content'] {
        width: 100%;
        container-type: inline-size;

        .navi-btn {
            width: 10px !important;
            min-width: 10px !important;
            min-height: 25px !important;
            padding: 0;
        }

        hr {
            border: none;
            border-top: 1px solid #cccccc;
        }

        h2 {
            font-weight: 400;
            font-size: 18px;
            color: #e9048b;
            margin-bottom: 20px;
        }

        a[href^="tel:"] {
            color: #747474;
            text-decoration: none;
        }

        .items {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            column-gap: 3rem;
            @container (max-width: 1000px) {
                grid-template-columns: repeat(2, 1fr);
            }
            @container (max-width: 600px) {
                grid-template-columns: 1fr;
            }
            .itemRow {
                min-height: 2.375rem;
                margin-top: 0 !important;
                display: flex;
                @media only screen and (min-width: 62em) {
                    &:nth-child(n+12) {
                        border-bottom: none;
                    }
                }

                .row:before {
                    display: none;
                }

                .row:after {
                    display: none;
                }

                & > section {
                    display: flex;
                    flex-grow: 1;

                    .table-grid {
                        padding: 0.25rem.063rem 0.25rem;
                        margin: 0 !important;
                        display: grid;
                        grid-template-columns: 2rem auto 9rem;
                        width: 100%;
                        column-gap: 0.5rem;

                        .col {
                            position: relative;
                            padding: 0 !important;
                            vertical-align: center;
                            display: flex;
                            justify-content: center;
                            flex-direction: column;

                            .no-wrap {
                                white-space: nowrap;
                            }

                            .v-layout {
                                display: flex;
                                gap: 0.7rem;

                                .v-img {
                                    max-width: 2rem !important;
                                }
                            }

                            section {
                                display: inline-block;

                                &[aria-label='ui-icon'] {
                                    height: 100%;
                                    display: flex;
                                    justify-content: center;
                                    align-items: center;
                                }
                            }

                            i {
                                transform: translateY(-1px);
                            }

                            .spaceholder-for-icon {
                                width: 1.063rem;
                                display: inline-block;
                            }

                            i,
                            .spaceholder-for-icon {
                                margin-right: 0.25rem;
                            }

                            img {
                                width: 2.25rem;
                                height: fit-content;
                            }

                            .status-open, .telephone, .info {
                                display: block;
                            }
                        }
                    }
                }
            }
        }

    }

    .centered-flex {
        height: 100%;
        display: flex !important;
        align-items: self-start;
    }

    .animated-element {
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.5s ease, visibility 0.5s ease;
    }

    .animated-element.is-visible {
        opacity: 1;
        visibility: visible;
    }
}
</style>
